import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { sanitizeDevExpressMarkupText } from '@app/modules/reports/components/reports/report-form-dialog/devexpress-markup-text';
import {
  autosomalAneuploidiesPattern,
  deletionDuplicationPattern,
  deletionDuplicationSyndromesPattern,
} from '@app/modules/reports/components/reports/report-form-dialog/validation-patterns';
import {
  GetReportAction,
  UpdateReportAction,
} from '@app/modules/reports/store/reports.actions';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import {
  InvalidValue,
  SupplementaryLaboratory,
  UpdateReportModel,
} from '@app/modules/reports/types/report.types';
import {
  Gender,
  PregnancyType,
} from '@app/modules/service-data/types/service-data.types';
import { LanguageState } from '@core/store/languages/languages.state';
import { ProductsListState } from '@core/store/products/products.state';
import { SigneeState } from '@core/store/signees/signees.state';
import { Util } from '@core/utils/core.util';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { QuillEditorComponent } from 'ngx-quill';

const GENETIC_VARIANTS_CONTROL_NAME = 'geneticVariants';
const GENETIC_VARIANT_FIELDS = [
  { controlName: 'gene' },
  { controlName: 'genomicLocation' },
  { controlName: 'refSeq' },
  { controlName: 'variant' },
  { controlName: 'condition' },
  { controlName: 'heredityModel' },
  { controlName: 'pathogenicity' },
  { controlName: 'ratio' },
];

@Component({
  selector: 'app-report-form-dialog',
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
    TranslatePipe,
    ValidationErrorComponent,
    DataTableComponent,
    QuillEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './report-form-dialog.component.html',
})
export class ReportFormDialogComponent {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private modal = inject(NgbActiveModal);
  public id = model('id');

  protected loading = signal(false);
  protected supplementaryLaboratorySelection = signal<string>(null);
  protected supplementaryLaboratoryOptions: string[] = Object.values(
    SupplementaryLaboratory
  ).filter(
    (supplementaryLaboratory) =>
      supplementaryLaboratory !== SupplementaryLaboratory.Other
  );

  protected report = computed(() =>
    this.store.selectSignal(ReportsState.getReport)()(this.id())
  );
  protected properties = computed(() =>
    Util.PropertiesToObject(this.report()?.properties ?? [])
  );
  protected product = computed(() =>
    this.store.selectSignal(ProductsListState.getProduct)()(
      this.properties().productid
    )
  );
  protected productName = computed(
    () => this.product()?.internalName ?? this.product()?.name ?? '/'
  );
  protected languages = this.store.selectSignal(LanguageState.getLanguages);
  protected signees = computed(() =>
    this.store.selectSignal(SigneeState.getSigneeListByLaboratory)()(
      Util.PropertiesToObject(this.report().properties).laboratory
    )
  );

  private invalidValues = computed(() => this.report().invalidValues);
  protected shownFields = computed(() =>
    (this.invalidValues() ?? [])
      .map((v) => v.transformsTo)
      .flat(1)
      .map((field) => field.toLowerCase())
  );

  invalidValuesDataTableConfiguration: DataTableConfiguration;
  protected interpretationEditorModules = {
    toolbar: [['bold', 'italic', 'underline', 'strike']],
  };
  protected interpretationEditorFormats = [
    'bold',
    'italic',
    'underline',
    'strike',
    'script',
  ];

  form = this.fb.group({
    id: ['', Validators.required],
    languageId: ['', Validators.required],
    signeeId: ['', Validators.required],
    analysisData: this.fb.group({
      autosomalAneuploidies: [
        '',
        [Validators.pattern(autosomalAneuploidiesPattern), Validators.required],
      ],
      deletionDuplication: [
        '',
        [Validators.pattern(deletionDuplicationPattern), Validators.required],
      ],
      deletionDuplicationSyndromes: [
        '',
        [
          Validators.pattern(deletionDuplicationSyndromesPattern),
          Validators.required,
        ],
      ],
      supplementaryLaboratoryInfo: [''],
      pregnancyType: ['', Validators.required],
      gender: ['', Validators.required],
      yChromosomeDetected: [false, Validators.required],
      diGeorgeSyndromeDetected: ['', Validators.required],
      ComplexSexChromosomeAbnormalities: ['', Validators.required],
      monoInterpretation: ['', Validators.required],
      geneticVariants: this.fb.array([]),
    }),
  });

  constructor() {
    effect(() => {
      this.store.dispatch(new GetReportAction(this.id()));
    });

    effect(() => {
      this.rebuildGeneticVariantsForm(this.report()?.invalidValues ?? []);
      this.form.patchValue(this.sanitizeReport(this.report()));
      this.patchGeneticVariantInvalidValues(this.report()?.invalidValues ?? []);
    });

    effect(() => {
      const supplementaryLaboratoryInfo =
        this.report()?.analysisData?.supplementaryLaboratoryInfo;
      const supplementaryLaboratoryValue =
        supplementaryLaboratoryInfo as string;

      if (!supplementaryLaboratoryValue) {
        this.supplementaryLaboratorySelection.set(null);
        return;
      }

      if (
        this.supplementaryLaboratoryOptions.includes(
          supplementaryLaboratoryValue
        )
      ) {
        this.supplementaryLaboratorySelection.set(supplementaryLaboratoryValue);
        return;
      }

      this.supplementaryLaboratorySelection.set(SupplementaryLaboratory.Other);
    });

    effect(() => {
      const report = untracked(() => this.report());

      for (const control of Object.keys(
        (this.form.get('analysisData') as FormGroup).controls
      )) {
        if (control === GENETIC_VARIANTS_CONTROL_NAME) {
          continue;
        }

        if (
          this.shownFields().includes(control.toLowerCase()) ||
          control === 'supplementaryLaboratoryInfo'
        ) {
          this.form.get('analysisData').get(control).enable();
        } else {
          this.form.get('analysisData').get(control).disable();
        }

        if (control === 'ComplexSexChromosomeAbnormalities') {
          this.form
            .get('analysisData')
            .get(control)
            .patchValue(
              report.invalidValues.find((v) => v.transformsTo.includes(control))
                ?.value
            );
        }
      }

      this.applyGeneticVariantVisibility();
    });

    this.invalidValuesDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      columns: [
        {
          label: 'Field',
          property: 'field',
        },
        {
          label: 'Value',
          property: 'value',
        },
      ],
      rows$: toObservable(this.invalidValues),
    };
  }

  protected c(control: string): AbstractControl {
    return this.form.controls[control];
  }

  dismiss() {
    this.modal.dismiss();
  }

  fieldShown(field) {
    return this.form.get('analysisData').get(field).enabled;
  }

  protected get geneticVariants(): FormArray<FormGroup> {
    return this.form.get(
      `analysisData.${GENETIC_VARIANTS_CONTROL_NAME}`
    ) as FormArray<FormGroup>;
  }

  protected geneticVariantFieldShown(index: number, controlName: string) {
    return this.geneticVariants.at(index).get(controlName).enabled;
  }

  protected onSupplementaryLaboratoryChange(value: SupplementaryLaboratory) {
    this.supplementaryLaboratorySelection.set(value);

    const control = this.form.get('analysisData.supplementaryLaboratoryInfo');
    const currentValue = control.value;

    if (value === SupplementaryLaboratory.Other) {
      if (
        !currentValue ||
        this.supplementaryLaboratoryOptions.includes(currentValue as string)
      ) {
        control.patchValue('');
      }

      return;
    }

    control.patchValue(value ?? '');
  }

  protected onInterpretationEditorCreated(editor: any) {
    editor.enable(true);
    editor.root.style.minHeight = '4.5rem';
    editor.root.style.width = '100%';
    editor.root.style.maxHeight = '15rem';
    editor.root.parentElement.style.height = 'auto';
  }

  submit() {
    if (this.form.valid) {
      this.loading.set(true);
      const report = this.sanitizeReport(
        this.normalizeGeneticVariantsPayload(
          this.form.value as UpdateReportModel
        )
      ) as UpdateReportModel;

      this.store
        .dispatch(new UpdateReportAction(report))
        .subscribe(() => this.modal.close());
    }
  }

  private sanitizeReport<T extends Partial<UpdateReportModel>>(report: T): T {
    if (!report?.analysisData?.monoInterpretation) {
      return report;
    }

    return {
      ...report,
      analysisData: {
        ...report.analysisData,
        monoInterpretation: sanitizeDevExpressMarkupText(
          report.analysisData.monoInterpretation
        ),
      },
    };
  }

  private rebuildGeneticVariantsForm(invalidValues: InvalidValue[]) {
    const variantCount = this.getGeneticVariantCount(invalidValues);

    while (this.geneticVariants.length < variantCount) {
      this.geneticVariants.push(this.createGeneticVariantFormGroup());
    }

    while (this.geneticVariants.length > variantCount) {
      this.geneticVariants.removeAt(this.geneticVariants.length - 1);
    }
  }

  private createGeneticVariantFormGroup() {
    return this.fb.group(
      GENETIC_VARIANT_FIELDS.reduce(
        (controls, field) => ({
          ...controls,
          [field.controlName]: [''],
        }),
        {}
      )
    );
  }

  private getGeneticVariantCount(invalidValues: InvalidValue[]) {
    const indexes = invalidValues
      .flatMap((invalidValue) => invalidValue.transformsTo)
      .map((transformsTo) =>
        this.getGeneticVariantTransformsToPath(transformsTo)
      )
      .filter((path) => path)
      .map(({ index }) => index);

    return indexes.length ? Math.max(...indexes) + 1 : 0;
  }

  private patchGeneticVariantInvalidValues(invalidValues: InvalidValue[]) {
    for (const invalidValue of invalidValues) {
      for (const transformsTo of invalidValue.transformsTo) {
        const path = this.getGeneticVariantTransformsToPath(transformsTo);

        if (!path) {
          continue;
        }

        const field = GENETIC_VARIANT_FIELDS.find(
          (variantField) =>
            variantField.controlName.toLowerCase() === path.field.toLowerCase()
        );

        if (!field) {
          continue;
        }

        this.geneticVariants
          .at(path.index)
          ?.get(field.controlName)
          ?.patchValue(invalidValue.value);
      }
    }
  }

  private applyGeneticVariantVisibility() {
    for (let index = 0; index < this.geneticVariants.length; index++) {
      for (const field of GENETIC_VARIANT_FIELDS) {
        const control = this.geneticVariants.at(index).get(field.controlName);
        const transformsTo = [
          GENETIC_VARIANTS_CONTROL_NAME,
          index,
          field.controlName,
        ]
          .join('.')
          .toLowerCase();

        if (this.shownFields().includes(transformsTo)) {
          control.enable();
        } else {
          control.disable();
        }
      }
    }
  }

  private getGeneticVariantTransformsToPath(transformsTo: string) {
    const [prefix, index, field] = transformsTo.split('.');

    if (
      prefix.toLowerCase() !== GENETIC_VARIANTS_CONTROL_NAME.toLowerCase() ||
      !Number.isInteger(Number(index)) ||
      !field
    ) {
      return null;
    }

    return {
      index: Number(index),
      field,
    };
  }

  private normalizeGeneticVariantsPayload(report: UpdateReportModel) {
    if (!report?.analysisData?.geneticVariants) {
      return report;
    }

    return {
      ...report,
      analysisData: {
        ...report.analysisData,
        geneticVariants: report.analysisData.geneticVariants.map((variant) => {
          if (!Object.prototype.hasOwnProperty.call(variant, 'ratio')) {
            return variant;
          }

          return {
            ...variant,
            ratio: this.normalizeRatio(variant.ratio),
          };
        }),
      },
    };
  }

  private normalizeRatio(ratio: string | number) {
    if (ratio === null || ratio === undefined || ratio === '') {
      return ratio;
    }

    if (typeof ratio === 'number') {
      return ratio;
    }

    return Number(ratio.replace('%', '').replace(',', '.'));
  }

  protected readonly PregnancyType = PregnancyType;
  protected readonly Object = Object;
  protected readonly Gender = Gender;
  protected readonly SupplementaryLaboratory = SupplementaryLaboratory;
  protected readonly GENETIC_VARIANT_FIELDS = GENETIC_VARIANT_FIELDS;
}
